
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Transaction, TransactionType, Household } from '../types';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { Trash2, Edit3, User, Users, ChevronDown, MessageSquare, AlertCircle, FileText, X, CheckCircle2, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const History: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'personal' | 'household'>('personal');
  const [activeHouseholdId, setActiveHouseholdId] = useState<string | null>(null);
  const [households, setHouseholds] = useState<Household[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [selectedReceipt, setSelectedReceipt] = useState<string | null>(null);
  const [settlingId, setSettlingId] = useState<string | null>(null);
  
  const [filterType, setFilterType] = useState<TransactionType | 'all'>('all');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const navigate = useNavigate();

  useEffect(() => { 
    fetchInitialData(); 
  }, []);

  useEffect(() => { 
    fetchTransactions(); 
  }, [filterType, selectedMonth, viewMode, activeHouseholdId]);

  const fetchInitialData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    setCurrentUserId(user.id);

    const { data: memberData } = await supabase.from('household_members').select('household_id').eq('user_id', user.id);
    if (memberData && memberData.length > 0) {
      const ids = memberData.map(m => m.household_id);
      const { data } = await supabase.from('households').select('*').in('id', ids);
      if (data) {
        setHouseholds(data);
        if (data.length > 0) setActiveHouseholdId(data[0].id);
      }
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const start = format(startOfMonth(new Date(selectedMonth)), 'yyyy-MM-dd');
    const end = format(endOfMonth(new Date(selectedMonth)), 'yyyy-MM-dd');

    let query = supabase.from('transactions').select('*').gte('date', start).lte('date', end).order('date', { ascending: false });

    if (viewMode === 'personal') query = query.is('household_id', null).eq('user_id', user.id);
    else if (activeHouseholdId) query = query.eq('household_id', activeHouseholdId);
    else { setTransactions([]); setLoading(false); return; }

    if (filterType !== 'all') query = query.eq('type', filterType);
    const { data } = await query;
    setTransactions(data || []);
    setLoading(false);
  };

  const handleSettle = async (e: React.MouseEvent, tx: Transaction) => {
    e.stopPropagation();
    if (!window.confirm(`Mark this debt as settled/paid?`)) return;
    
    setSettlingId(tx.id);
    try {
      const updatedNote = tx.note 
        ? `${tx.note} (Settled on ${format(new Date(), 'dd MMM')})` 
        : `Settled on ${format(new Date(), 'dd MMM')}`;
        
      const { error } = await supabase
        .from('transactions')
        .update({ is_settled: true, note: updatedNote })
        .eq('id', tx.id);
        
      if (error) throw error;
      
      // Update local state to reflect the change immediately
      setTransactions(prev => prev.map(t => t.id === tx.id ? { ...t, is_settled: true, note: updatedNote } : t));
    } catch (err: any) {
      alert("Settlement update failed: " + err.message);
    } finally {
      setSettlingId(null);
    }
  };

  return (
    <div className="space-y-6 pb-20">
      {selectedReceipt && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-6 backdrop-blur-md" onClick={() => setSelectedReceipt(null)}>
          <button className="absolute top-10 right-10 text-white p-4 hover:scale-110 transition-transform"><X size={32} /></button>
          <img src={selectedReceipt} className="max-w-full max-h-[80vh] rounded-3xl shadow-2xl border border-white/10" alt="Receipt" />
          <p className="mt-8 text-white/60 font-black text-xs uppercase tracking-widest">Tap anywhere to close</p>
        </div>
      )}

      <header className="px-1"><h1 className="text-2xl font-black text-slate-900">History</h1></header>

      <div className="flex bg-slate-200/50 p-1.5 rounded-[2.5rem] border border-slate-200 shadow-inner">
        <button onClick={() => setViewMode('personal')} className={`flex-1 py-4 rounded-[2rem] text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'personal' ? 'bg-[#0f172a] text-white shadow-xl' : 'text-slate-500'}`}>Personal</button>
        {households.length > 0 && <button onClick={() => setViewMode('household')} className={`flex-1 py-4 rounded-[2rem] text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'household' ? 'bg-[#0f172a] text-white shadow-xl' : 'text-slate-500'}`}>Groups</button>}
      </div>

      <div className="bg-white p-6 rounded-[3rem] shadow-xl border border-slate-100 flex flex-col gap-4">
        <select className="w-full px-6 py-4 border-none bg-slate-50 rounded-[1.8rem] outline-none font-bold appearance-none cursor-pointer" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
          {Array.from({ length: 12 }).map((_, i) => {
            const d = subMonths(new Date(), i);
            return <option key={i} value={format(d, 'yyyy-MM')}>{format(d, 'MMMM yyyy')}</option>;
          })}
        </select>
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {['all', 'expense', 'income', 'debt', 'receivable'].map(t => (
            <button key={t} onClick={() => setFilterType(t as any)} className={`px-5 py-3 rounded-2xl text-[10px] font-black uppercase border transition-all shrink-0 ${filterType === t ? 'bg-[#0f172a] text-white border-transparent' : 'bg-white text-slate-400 border-slate-100'}`}>
              {t === 'debt' ? 'Borrowed' : t === 'receivable' ? 'Lent' : t}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {loading ? <div className="py-20 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div> : 
          transactions.map(tx => {
            const isDebtOrReceivable = tx.type === 'debt' || tx.type === 'receivable';
            const canEdit = tx.user_id === currentUserId;

            return (
              <div key={tx.id} className="bg-white p-8 rounded-[3.5rem] border border-slate-100 shadow-sm flex flex-col hover:border-slate-300 transition-all group">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex items-center flex-1">
                    <div className={`w-14 h-14 rounded-3xl flex items-center justify-center mr-5 ${tx.type === 'expense' || tx.type === 'debt' ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-600'}`}>
                      <span className="text-[10px] font-black uppercase">{tx.type.substring(0,3)}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-black text-slate-900 text-lg leading-tight">{tx.category}</p>
                        {isDebtOrReceivable && (
                          tx.is_settled ? (
                            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[8px] font-black uppercase rounded-lg border border-emerald-100 flex items-center gap-1">
                              <CheckCircle2 size={10} /> Settled
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-[8px] font-black uppercase rounded-lg border border-amber-100 flex items-center gap-1">
                              <Clock size={10} /> Pending
                            </span>
                          )
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 font-black uppercase mt-1">
                        {format(new Date(tx.date), 'dd MMM yyyy')} • {tx.type === 'debt' ? 'Borrowed' : tx.type === 'receivable' ? 'Lent' : tx.type}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-black text-xl ${tx.type === 'expense' || tx.type === 'debt' ? 'text-red-600' : 'text-emerald-700'}`}>
                      ₹{Number(tx.amount).toLocaleString()}
                    </p>
                  </div>
                </div>

                {tx.note && (
                  <div className="mt-4 px-5 py-4 bg-slate-50/80 rounded-2xl italic text-xs text-slate-500 border border-slate-100">
                    {tx.note}
                  </div>
                )}

                <div className="mt-6 flex items-center justify-between border-t border-slate-50 pt-5">
                  <div className="flex gap-2">
                    {tx.receipt_url && (
                      <button onClick={() => setSelectedReceipt(tx.receipt_url!)} className="p-3 bg-amber-50 text-amber-600 rounded-2xl hover:bg-amber-100 transition-colors"><FileText size={18} /></button>
                    )}
                    {canEdit && (
                      <button onClick={() => navigate(`/edit/${tx.id}`)} className="p-3 bg-slate-50 text-slate-400 rounded-2xl hover:text-slate-900 hover:bg-slate-100 transition-colors"><Edit3 size={18} /></button>
                    )}
                  </div>

                  {isDebtOrReceivable && !tx.is_settled && canEdit && (
                    <button 
                      onClick={(e) => handleSettle(e, tx)}
                      disabled={settlingId === tx.id}
                      className="flex items-center gap-2 px-6 py-3 bg-[#0f172a] text-[#fbbf24] rounded-[1.5rem] text-[10px] font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all disabled:opacity-50"
                    >
                      {settlingId === tx.id ? <div className="w-4 h-4 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin"></div> : <CheckCircle2 size={14} />}
                      Mark as Paid
                    </button>
                  )}
                  
                  {isDebtOrReceivable && !tx.is_settled && !canEdit && (
                    <div className="flex items-center gap-2 px-6 py-3 bg-slate-50 text-slate-400 rounded-[1.5rem] text-[9px] font-black uppercase border border-slate-100 italic">
                      Waiting for owner
                    </div>
                  )}
                </div>
              </div>
            );
          })
        }
        {!loading && transactions.length === 0 && (
          <div className="text-center py-20 bg-white rounded-[3rem] border border-dashed border-slate-200">
             <AlertCircle size={40} className="mx-auto text-slate-100 mb-4" />
             <p className="text-slate-300 font-black text-[10px] uppercase tracking-widest">No entries found for this period</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
